import { createContext, useContext, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, linkOptions, useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import { IconCircleCheckFill18 } from "nucleo-ui-fill-18"
import { IconSplitOutline18, IconStackOutline18 } from "nucleo-ui-outline-18"

import { Button, Popover } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { useSlackConnections } from "@/lib/zero"
import { formatGroupingWindow, groupingWindowMinutes } from "./slack-grouping"

export interface JoinedSlackMessage {
	id: string
	createdAt: number
	authorName: string
	text: string
}

interface SlackGroupingState {
	threadId: string
	slackChannelId: string
	joined: JoinedSlackMessage[]
}

export const SlackGroupingContext = createContext<SlackGroupingState | null>(
	null,
)

const plainText = (html: string) =>
	html
		.replace(/<[^>]+>/g, " ")
		.replace(/&nbsp;/g, " ")
		.replace(/\s+/g, " ")
		.trim()

/** The row above the first message that joined a grouped Slack thread: why it joined, and a way to move it out. */
export function SlackGroupingNotice({
	beforeMessageId,
}: {
	beforeMessageId: string
}) {
	const state = useContext(SlackGroupingContext)
	if (!state || state.joined[0]?.id !== beforeMessageId) return null
	return <GroupingRow state={state} />
}

function GroupingRow({ state }: { state: SlackGroupingState }) {
	const { role } = useRole()
	const [mode, setMode] = useState<"why" | "split" | null>(null)
	const [channel] = useQuery(
		queries.slackChannelByChannelId({ channelId: state.slackChannelId }),
	)
	const connections = useSlackConnections()
	const connection =
		connections.find((row) => row.id === channel?.slackConnectSettingsId) ??
		connections[0]
	const ownWindow = channel?.groupingWindowMinutes ?? null
	const minutes = groupingWindowMinutes({
		channelMinutes: ownWindow,
		workspaceMinutes: connection?.groupingWindowMinutes,
	})
	const windowLabel = formatGroupingWindow(minutes)
	const channelName = channel?.name ? `#${channel.name}` : "this channel"
	const settingsLink = slackSettingsLink(
		ownWindow != null && connection ? connection.id : undefined,
	)
	const count = state.joined.length

	return (
		<div className="flex items-center gap-2 py-1 text-sm">
			<IconStackOutline18 className="h-4 w-4 flex-none text-tertiary" />
			<span className="min-w-0 flex-1 truncate text-secondary">
				<span className="font-medium text-primary">
					{count} message{count === 1 ? "" : "s"}
				</span>{" "}
				joined this thread, sent within {windowLabel}
			</span>
			<Popover
				open={mode !== null}
				onOpenChange={(open) => {
					if (!open) setMode(null)
				}}
				align="end"
				className="w-[26rem] p-0"
				content={
					<GroupingPanel
						state={state}
						channelName={channelName}
						windowLabel={windowLabel}
						isChannelOverride={ownWindow != null}
						settingsLink={settingsLink}
						mode={mode ?? "why"}
						onDone={() => setMode(null)}
					/>
				}
			>
				<div className="flex flex-none items-center gap-1">
					<Button variant="ghost" size="sm" onClick={() => setMode("why")}>
						Why?
					</Button>
					{role !== "VIEWER" && (
						<Button
							variant="ghost"
							size="sm"
							Icon={IconSplitOutline18}
							onClick={() => setMode("split")}
						>
							Split
						</Button>
					)}
				</div>
			</Popover>
		</div>
	)
}

function GroupingPanel({
	state,
	channelName,
	windowLabel,
	isChannelOverride,
	settingsLink,
	mode,
	onDone,
}: {
	state: SlackGroupingState
	channelName: string
	windowLabel: string
	isChannelOverride: boolean
	settingsLink: ReturnType<typeof slackSettingsLink>
	mode: "why" | "split"
	onDone: () => void
}) {
	const navigate = useNavigate()
	const utils = api.useUtils()
	const [picked, setPicked] = useState<Set<string>>(
		() => new Set(state.joined[0] ? [state.joined[0].id] : []),
	)
	const split = api.thread.split.useMutation({
		onSuccess: ({ newThreadId }) => {
			const count = picked.size
			onDone()
			toast.success({
				title: `Moved ${count} message${count === 1 ? "" : "s"} to a new thread`,
				descriptionAction: {
					label: "Open thread",
					callback: () =>
						void navigate({ to: "/threads/$id", params: { id: newThreadId } }),
				},
			})
			void utils.workspace.searchResults.invalidate()
		},
		onError: (error) => toast.error({ title: error.message }),
	})
	const toggle = (id: string) =>
		setPicked((current) => {
			const next = new Set(current)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	const submit = () => {
		if (picked.size === 0 || split.isLoading) return
		split.mutate({ threadId: state.threadId, slackMessageIds: [...picked] })
	}

	return (
		<div className="flex flex-col text-sm">
			{mode === "why" && (
				<div className="flex flex-col gap-3 p-4">
					<div className="flex flex-col gap-1">
						<div className="font-medium text-primary">
							Grouped by your Slack setting
						</div>
						<p className="text-secondary">
							These were sent within {windowLabel} of the thread&apos;s first
							message in {channelName}, so they joined the same thread.
						</p>
					</div>
					<div className="flex items-start justify-between gap-3 rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-800">
						<div className="min-w-0">
							<div className="font-medium text-primary">Message grouping</div>
							<div className="truncate text-xs text-tertiary">
								{isChannelOverride
									? `Set for ${channelName}`
									: `Workspace default, used by ${channelName}`}
							</div>
						</div>
						<div className="flex flex-none flex-col items-end">
							<span className="font-medium tabular-nums text-primary">
								{windowLabel}
							</span>
							<Link
								{...settingsLink}
								className="text-xs text-blue-600 hover:underline dark:text-blue-400"
							>
								Change
							</Link>
						</div>
					</div>
				</div>
			)}

			{mode === "split" && (
				<div className="flex flex-col gap-2 p-4">
					<div className="text-xs text-tertiary">Move to a new thread</div>
					<div className="flex max-h-64 flex-col gap-1 overflow-y-auto pb-1">
						{state.joined.map((message) => {
							const checked = picked.has(message.id)
							return (
								<button
									key={message.id}
									type="button"
									role="checkbox"
									aria-checked={checked}
									onClick={() => toggle(message.id)}
									className="flex items-start gap-2.5 rounded-md p-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
								>
									{checked ? (
										<IconCircleCheckFill18 className="mt-0.5 h-4 w-4 flex-none text-primary" />
									) : (
										<span className="mt-0.5 h-4 w-4 flex-none rounded-full border border-gray-300 dark:border-gray-600" />
									)}
									<span className="min-w-0 flex-1">
										<span className="flex items-baseline gap-1.5">
											<span className="truncate font-medium text-primary">
												{message.authorName}
											</span>
											<span className="flex-none text-xs tabular-nums text-tertiary">
												{format(message.createdAt, "HH:mm")}
											</span>
										</span>
										<span className="line-clamp-2 text-secondary">
											{plainText(message.text)}
										</span>
									</span>
								</button>
							)
						})}
					</div>
					<div className="flex justify-end gap-2 pt-1">
						<Button variant="ghost" size="sm" onClick={onDone}>
							Cancel
						</Button>
						<Button
							size="sm"
							disabled={picked.size === 0}
							isLoading={split.isLoading}
							onClick={submit}
						>
							Move {picked.size} message{picked.size === 1 ? "" : "s"}
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}

function slackSettingsLink(channelConnectionId: string | undefined) {
	return channelConnectionId
		? linkOptions({
				to: "/settings/integrations/slack/$connectionId/channels",
				params: { connectionId: channelConnectionId },
			})
		: linkOptions({ to: "/settings/integrations/slack" })
}
